<template>
  <div class="promotion-flow">
    <!-- Main Content -->
    <template v-if="loading">
      <div class="d-flex justify-content-center h-100">
        <b-spinner variant="primary" />
      </div>
    </template>
    <template v-else>
      <b-container fluid v-if="hasPromotion">
        <b-row>
          <!-- Sidebar Menu -->
          <b-col md="3" class="sidebar">
            <div class="header">
              <h4>{{ consultant.name }}</h4>
              <span>{{ promotionTitle }}</span>
            </div>

            <PromotionStepMenuItem
              v-for="step in uniqueSteps"
              :key="step.step"
              :step="step"
              :current-step="currentStep"
              :completed="isCompleted"
              @click="goToStep(step.step)"
            />

            <div class="progress-container">
              <b-progress
                :value="progress"
                max="100"
                animated
                variant="success"
              />
            </div>
            <p class="text-muted mt-1">{{ progress || 0 }}% completo</p>
          </b-col>

          <!-- Content Area -->
          <b-col md="9" class="content-area" v-if="hasPromotion">
            <PromotionStep
              :items="currentStepItems"
              :title="currentStepMeta.title"
              :subtitle="currentStepMeta.subtitle"
              buttonText="Continuar"
              :consultantOptions="consultantList"
              @next="goToNextStep"
              :allow-continue="canContinue"
            />
          </b-col>
        </b-row>
      </b-container>
      <div v-else class="d-flex justify-content-center h-100">
        <h5>Não há nenhuma promoção pendente para o consultor</h5>
      </div>
    </template>
  </div>
</template>

<script>
import PromotionStep from "../components/PromotionStep.vue";
import PromotionStepMenuItem from "../components/PromotionStepMenuItem.vue";
import { mapActions, mapGetters } from "vuex";
import * as promoTypes from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import * as consultantTypes from "@/modules/consultant/store/types";
import { BContainer, BRow, BCol, BProgress, BOverlay } from "bootstrap-vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { BUY_EXPANSAO_FRANCHISE, BUY_PLANEJADOR_FRANCHISE } from "@/constants/promotion_reason_types";

export default {
  components: {
    PromotionStep,
    PromotionStepMenuItem,
    BContainer,
    BRow,
    BCol,
    BProgress,
    BOverlay,
  },

  data() {
    return {
      loading: true,
      hasPromotion: true,
    };
  },

  setup() {
    return { toast: useToast() };
  },

  computed: {
    // 1) promotion module getters
    ...mapGetters({
      status: promoTypes.GET_STATUS,
      rawSteps: promoTypes.GET_STEPS,
      isCompleted: promoTypes.IS_COMPLETED,
      progress: promoTypes.PROGRESS,
      consultantList: sharedTypes.CONSULTANTS,
    }),

    // normalize so we always use `.step` and `.items`
    normalizedSteps() {
      return this.rawSteps.map((s, index) => ({
        step: s.step ?? s.number,
        items: s.items ?? [],
        number:
          s.step ??
          s.number ??
          `${index.length < 2 ? index + 1 : `0${index + 1}`}`,
        title: `Passo ${index.length < 2 ? index + 1 : `0${index + 1}`}`,
      }));
    },

    uniqueSteps() {
      const seen = new Set();
      return this.normalizedSteps.filter((s) => {
        if (seen.has(s.step)) return false;
        seen.add(s.step);
        return true;
      });
    },

    currentStep() {
      return this.status.current_step;
    },

    currentStepItems() {
      const s = this.normalizedSteps?.find((s) => s.step === this.currentStep);
      return s ? s.items : [];
    },

    currentStepMeta() {
      const s =
        this.normalizedSteps?.find((s) => s.step === this.currentStep) || {};
      return { title: s.title, subtitle: s.subtitle };
    },

    hasNextStep() {
      return this.normalizedSteps.some((s) => s.step === this.currentStep + 1);
    },

    consultant() {
      return {
        id: this.status.consultant_id,
        name: this.status.consultant_name,
      };
    },

    targetRole() {
      return {
        id: this.status.target_role_id,
        name: this.status.target_role,
        description: this.status.target_role_description,
      };
    },

    promotionTitle() {
      if(this.targetRole && this.targetRole.name){
        return `Promoção para ${this.targetRole.name}`;
      }else if(this.status.promotion_reason){
        return this.status.promotion_reason
      }
      return 'Compra de franquia pendente';
    },

    canContinue(){
      if((this.status.promotion_reason_id == BUY_PLANEJADOR_FRANCHISE || 
        this.status.promotion_reason_id == BUY_EXPANSAO_FRANCHISE) && 
        this.status.current_step >= 2
      ) {
        return false
      }
      return true
    }
  },

  mounted() {
  },

  methods: {
    ...mapActions({
      initialize: promoTypes.INITIALIZE_PROMOTION,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      changeConsultantRole: consultantTypes.CHANGE_CONSULTANT_ROLE,
    }),

    goToStep(step) {
      if (!this.isCompleted && step < this.currentStep) {
        this.reload();
      }
    },

    goToNextStep() {
      if((this.status.promotion_reason_id == BUY_PLANEJADOR_FRANCHISE || 
        this.status.promotion_reason_id == BUY_EXPANSAO_FRANCHISE) && 
        this.status.current_step == 1
      ) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: 'Sucesso',
            text: `Os dados foram salvos com sucesso. Em até 10 dias após a assinatura destes e-mails, você receberá um e-mail contendo o contrato para assinatura.`,
            icon: 'CoffeeIcon',
            variant: 'success',
          },
        })
      }
      this.reload()
    },

    reload() {
      this.loading = true;
      this.initialize({
        consultantId: this.$route.params.id,
        targetRoleId: this.status.target_role_id,
        promotionReasonId: this.status.promotion_reason_id,
      })
        .catch((err) => {
          if (err.response?.status == 404) {
            this.hasPromotion = false;
          } else {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text: "Ocorreu um erro ao tentar carregar os passos de promoção. Tente novamente mais tarde.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },

  async created() {
    this.reload();
    this.getConsultants().catch((err) => {
      this.toast({
        component: ToastificationContent,
        props: {
          title: "Oops!",
          text: "Ocorreu um erro ao carregar os parceiros de negócio para seleção. Entre em contato com o setor de TI.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    });
  },
};
</script>

<style lang="scss" scoped>
.promotion-flow {
  min-height: 100vh;
}

.sidebar {
  padding: 0;
  min-height: 100vh;

  .header {
    margin-bottom: 1rem;
  }
}

.content-area {
  padding: 0 2rem 2rem;
  min-height: 100vh;
}

.progress-container {
  margin-top: 1rem;
}
</style>
